import ReactDOM from 'react-dom/client';
import { createBrowserRouter, RouterProvider, Outlet, useLocation, Navigate } from 'react-router-dom';
import Header from './components/Header/Header';
import Footer from './components/Footer/Footer';
import Main from './components/Main/Main';
import Error from './components/Error';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import UserContext from './utils/userContext';
import About from './components/pages/About/About';
import Beliefs from './components/pages/Beliefs/Beliefs';
import Contact from './components/pages/Contact/Contact';
import Disclaimer from './components/pages/Disclaimer/Disclaimer';
import Privacy from './components/pages/Privacy/Privacy';
import School from './components/pages/School/School';
import Service from './components/pages/Service/Service';
import NotFound from './components/pages/NotFound/NotFound';
import { DEFAULT_LANG, ROUTE_SLUGS, getLangFromPath, getRoutePaths, matchRouteKey, switchLangPath } from './utils/i18n';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../index.css';
import ScrollToTop from './components/ScrollToTop';

const SITE_TITLE = "Monetique";
const SITE_URL = "https://www.monetiquepro.com";
const SITE_LOGO = `${SITE_URL}/assets/logo.png`;
const SITE_OG_IMAGE = `${SITE_URL}/assets/OG-banner.jpg`;
const GLOBAL_POSITIONING_TEXT = "Monetique is a strategic finance consulting firm in Sao Paulo, Brazil, helping companies improve profitability through FP&A, CFO advisory, financial planning, and AI-driven finance transformation.";

const ROUTE_META = {
    en: {
        home: {
            title: "Monetique | Strategic Finance Consulting, FP&A & CFO Advisory",
            description:
                "Drive profitable growth with strategic finance consulting, FP&A, CFO advisory, and AI-powered finance transformation tailored for scaling companies.",
        },
        about: {
            title: "About Monetique | Strategic Finance & CFO Advisory Experts",
            description:
                "Meet Monetique's senior finance experts in FP&A, business partnering, and performance strategy helping companies scale with clarity, control, and speed.",
        },
        beliefs: {
            title: "What We Believe | Monetique",
            description: "Explore the nine principles that guide Monetique: clarity, better decisions, purposeful technology, human partnership and sustainable growth.",
        },
        services: {
            title: "Finance Consulting Services | FP&A, CFO Advisory & AI Finance",
            description:
                "Explore strategic finance consulting services: FP&A, CFO advisory, executive business partnering, AI finance automation, and global expansion support.",
        },
        school: {
            title: "Monetique School | Executive Communication & Business English",
            description:
                "Strengthen executive communication, leadership, and business English skills through practical programs designed for finance and business professionals.",
        },
        contact: {
            title: "Contact Monetique | Book a Strategic Finance Consultation",
            description:
                "Talk to Monetique specialists in strategic finance, FP&A, and CFO advisory. Book your consultation via phone, email, WhatsApp, or Calendly.",
        },
        privacy: {
            title: "Privacy Policy | Monetique Strategic Finance Consulting",
            description:
                "Read Monetique's privacy policy covering LGPD compliance, Calendly scheduling data, information handling, and your legal privacy rights.",
        },
        disclaimer: {
            title: "Legal Disclaimer | Monetique Finance Consulting Services",
            description:
                "Review legal terms for Monetique's finance consulting services, including guidance scope, liability limits, and third-party tool usage.",
        },
        notFound: {
            title: "Page Not Found | Monetique Strategic Finance",
            description:
                "This page is unavailable. Explore Monetique's strategic finance consulting pages for services, team information, and consultation booking.",
        },
    },
    pt: {
        home: {
            title: "Monetique | Consultoria Financeira Estrategica, FP&A e CFO Advisory",
            description:
                "Acelere o crescimento lucrativo com consultoria financeira estrategica, FP&A, CFO advisory e transformacao financeira com IA para empresas em escala.",
        },
        about: {
            title: "Sobre a Monetique | Especialistas em Financas Estrategicas e CFO Advisory",
            description:
                "Conheca os especialistas da Monetique em FP&A, business partnering e estrategia de performance para escalar empresas com clareza e controle.",
        },
        beliefs: {
            title: "O que acreditamos | Monetique",
            description: "Conheça os nove princípios que orientam a Monetique: clareza, melhores decisões, tecnologia com propósito, parceria humana e crescimento sustentável.",
        },
        services: {
            title: "Servicos de Consultoria Financeira | FP&A, CFO Advisory e IA",
            description:
                "Conheca nossos servicos de financas estrategicas: FP&A, CFO advisory, parceria executiva, automacao financeira com IA e apoio a expansao global.",
        },
        school: {
            title: "Monetique School | Comunicacao Executiva e Business English",
            description:
                "Desenvolva comunicacao executiva, lideranca e business English com programas praticos para profissionais de financas e negocios.",
        },
        contact: {
            title: "Contato Monetique | Agende uma Consultoria Financeira Estrategica",
            description:
                "Fale com especialistas da Monetique em financas estrategicas, FP&A e CFO advisory. Agende sua consultoria por telefone, e-mail, WhatsApp ou Calendly.",
        },
        privacy: {
            title: "Politica de Privacidade | Monetique Consultoria Financeira",
            description:
                "Leia a politica de privacidade da Monetique com diretrizes de LGPD, dados de agendamento via Calendly, tratamento de informacoes e direitos do titular.",
        },
        disclaimer: {
            title: "Aviso Legal | Monetique Servicos de Consultoria Financeira",
            description:
                "Consulte os termos legais da Monetique, incluindo escopo consultivo, limites de responsabilidade e uso de ferramentas de terceiros.",
        },
        notFound: {
            title: "Pagina nao encontrada | Monetique Consultoria Financeira",
            description:
                "Esta pagina nao esta disponivel. Acesse os conteudos da Monetique sobre servicos, equipe e agendamento de consultoria.",
        },
    },
};

const ROUTE_LABELS = {
    en: {
        home: 'Home',
        about: 'About',
        beliefs: 'What We Believe',
        services: 'Services',
        school: 'Monetique School',
        contact: 'Contact',
        privacy: 'Privacy Policy',
        disclaimer: 'Legal Disclaimer',
    },
    pt: {
        home: 'Inicio',
        about: 'Sobre',
        beliefs: 'O que acreditamos',
        services: 'Servicos',
        school: 'Monetique School',
        contact: 'Contato',
        privacy: 'Politica de Privacidade',
        disclaimer: 'Aviso Legal',
    },
};

const ORGANIZATION_SCHEMA = {
    '@type': 'ProfessionalService',
    '@id': `${SITE_URL}/#organization`,
    name: SITE_TITLE,
    legalName: 'Monetique Estratégia e Gestão Financeira Ltda.',
    description: GLOBAL_POSITIONING_TEXT,
    url: `${SITE_URL}/`,
    logo: {
        '@type': 'ImageObject',
        url: SITE_LOGO,
    },
    image: SITE_OG_IMAGE,
    telephone: '+55 11 94479-4757',
    email: 'info@monetiquepro.com',
    address: {
        '@type': 'PostalAddress',
        streetAddress: 'Avenida Brigadeiro Faria Lima, 1811, Sala 1119',
        addressLocality: 'Sao Paulo',
        addressRegion: 'SP',
        addressCountry: 'BR',
    },
    areaServed: [
        { '@type': 'Country', name: 'Brazil' },
        { '@type': 'Country', name: 'United States' },
    ],
    serviceType: [
        'Strategic Finance Consulting',
        'FP&A',
        'CFO Advisory',
        'AI Finance Transformation',
        'Business Partnering',
        'Global Expansion Advisory',
    ],
    sameAs: [
        'https://www.linkedin.com/company/monetique/',
        'https://wa.me/5511944794757',
    ],
};

const TEAM_SCHEMA = [
    {
        name: 'Luciana Camargo',
        jobTitle: 'CEO & Founder | Strategic CFO',
        id: 'luciana-camargo',
    },
    {
        name: 'Gabrielle Doca',
        jobTitle: 'Senior FP&A Manager | Executive Mentor',
        id: 'gabrielle-doca',
    },
    {
        name: 'Gabriel Hildebrando',
        jobTitle: 'Finance Manager',
        id: 'gabriel-hildebrando',
    },
];

const normalizePath = (pathname) => pathname.replace(/\/+$/, '') || '/';
const toAbsoluteUrl = (path) => new URL(path, `${SITE_URL}/`).toString();

const AppLayout = () => {
    const [user, setUser] = useState("default");
    const location = useLocation();
    const appRef = useRef(null);

    useEffect(() => {
        if ('scrollRestoration' in window.history) {
            window.history.scrollRestoration = 'manual';
        }
    }, []);

    useEffect(() => {
        const root = document.documentElement;
        const finePointerMedia = window.matchMedia('(hover: hover) and (pointer: fine)');
        const reducedMotionMedia = window.matchMedia('(prefers-reduced-motion: reduce)');
        const fallbackX = Math.round(window.innerWidth * 0.52);
        const fallbackY = Math.round(window.innerHeight * 0.32);
        let rafId = null;
        let pointerX = fallbackX;
        let pointerY = fallbackY;

        const writePointerVars = () => {
            root.style.setProperty('--gold-spotlight-x', `${pointerX}px`);
            root.style.setProperty('--gold-spotlight-y', `${pointerY}px`);
            rafId = null;
        };

        const queuePointerWrite = () => {
            if (rafId === null) {
                rafId = window.requestAnimationFrame(writePointerVars);
            }
        };

        const isCursorSpotlightEnabled = () => finePointerMedia.matches && !reducedMotionMedia.matches;

        const setSpotlightMode = () => {
            const useCursorSpotlight = isCursorSpotlightEnabled();
            root.classList.toggle('cursor-spotlight-active', useCursorSpotlight);
            root.style.setProperty('--gold-spotlight-opacity', useCursorSpotlight ? '0.08' : '0');
            if (!useCursorSpotlight) {
                pointerX = Math.round(window.innerWidth * 0.52);
                pointerY = Math.round(window.innerHeight * 0.32);
                queuePointerWrite();
            }
        };

        const handlePointerMove = (event) => {
            if (!isCursorSpotlightEnabled()) {
                return;
            }
            pointerX = event.clientX;
            pointerY = event.clientY;
            queuePointerWrite();
        };

        const handlePointerLeave = () => {
            if (!isCursorSpotlightEnabled()) {
                return;
            }
            pointerX = Math.round(window.innerWidth * 0.52);
            pointerY = Math.round(window.innerHeight * 0.32);
            queuePointerWrite();
        };

        const handleResize = () => {
            if (!finePointerMedia.matches || reducedMotionMedia.matches) {
                pointerX = Math.round(window.innerWidth * 0.52);
                pointerY = Math.round(window.innerHeight * 0.32);
                queuePointerWrite();
            }
        };

        root.style.setProperty('--gold-spotlight-x', `${pointerX}px`);
        root.style.setProperty('--gold-spotlight-y', `${pointerY}px`);
        setSpotlightMode();

        window.addEventListener('pointermove', handlePointerMove, { passive: true });
        window.addEventListener('pointerleave', handlePointerLeave);
        window.addEventListener('resize', handleResize);
        finePointerMedia.addEventListener('change', setSpotlightMode);
        reducedMotionMedia.addEventListener('change', setSpotlightMode);

        return () => {
            if (rafId !== null) {
                window.cancelAnimationFrame(rafId);
            }
            window.removeEventListener('pointermove', handlePointerMove);
            window.removeEventListener('pointerleave', handlePointerLeave);
            window.removeEventListener('resize', handleResize);
            finePointerMedia.removeEventListener('change', setSpotlightMode);
            reducedMotionMedia.removeEventListener('change', setSpotlightMode);
            root.classList.remove('cursor-spotlight-active');
            root.style.removeProperty('--gold-spotlight-x');
            root.style.removeProperty('--gold-spotlight-y');
            root.style.removeProperty('--gold-spotlight-opacity');
        };
    }, []);

    useEffect(() => {
        const setMeta = (key, value, isProperty = false) => {
            const selector = isProperty
                ? `meta[property="${key}"]`
                : `meta[name="${key}"]`;
            let tag = document.head.querySelector(selector);
            if (!tag) {
                tag = document.createElement('meta');
                tag.setAttribute(isProperty ? 'property' : 'name', key);
                document.head.appendChild(tag);
            }
            tag.setAttribute('content', value);
        };

        const setLink = ({ rel, href, hreflang = null }) => {
            const selector = hreflang
                ? `link[rel="${rel}"][hreflang="${hreflang}"]`
                : `link[rel="${rel}"]:not([hreflang])`;
            let link = document.head.querySelector(selector);
            if (!link) {
                link = document.createElement('link');
                link.setAttribute('rel', rel);
                if (hreflang) {
                    link.setAttribute('hreflang', hreflang);
                }
                document.head.appendChild(link);
            }
            link.setAttribute('href', href);
        };

        const removeLink = ({ rel, hreflang = null }) => {
            const selector = hreflang
                ? `link[rel="${rel}"][hreflang="${hreflang}"]`
                : `link[rel="${rel}"]:not([hreflang])`;
            const link = document.head.querySelector(selector);
            if (link) {
                link.remove();
            }
        };

        const setStructuredData = (data) => {
            let script = document.head.querySelector('script[data-seo="structured-data"]');
            if (!script) {
                script = document.createElement('script');
                script.setAttribute('type', 'application/ld+json');
                script.setAttribute('data-seo', 'structured-data');
                document.head.appendChild(script);
            }
            script.textContent = JSON.stringify(data);
        };

        const lang = getLangFromPath(location.pathname);
        const canonicalPath = normalizePath(location.pathname || '/');
        const routeKey = matchRouteKey(lang, canonicalPath);
        const metaKey = routeKey || 'notFound';
        const metaCollection = ROUTE_META[lang] || ROUTE_META[DEFAULT_LANG];
        const meta = metaCollection[metaKey] || ROUTE_META[DEFAULT_LANG].home;
        const pathsForLang = getRoutePaths(lang);
        const enPaths = getRoutePaths('en');
        const ptPaths = getRoutePaths('pt');
        const canonicalUrl = toAbsoluteUrl(canonicalPath);
        const shouldIndex = Boolean(routeKey);
        const enUrl = routeKey ? toAbsoluteUrl(enPaths[routeKey]) : toAbsoluteUrl(switchLangPath(canonicalPath, 'en'));
        const ptUrl = routeKey ? toAbsoluteUrl(ptPaths[routeKey]) : toAbsoluteUrl(switchLangPath(canonicalPath, 'pt'));
        const locale = lang === 'pt' ? 'pt_BR' : 'en_US';
        const alternateLocale = lang === 'pt' ? 'en_US' : 'pt_BR';
        const languageTag = lang === 'pt' ? 'pt-BR' : 'en';
        const graph = [
            { ...ORGANIZATION_SCHEMA },
            {
                '@type': 'WebSite',
                '@id': `${SITE_URL}/#website`,
                name: SITE_TITLE,
                url: `${SITE_URL}/`,
                inLanguage: ['en', 'pt-BR'],
                publisher: { '@id': `${SITE_URL}/#organization` },
            },
            {
                '@type': 'WebPage',
                '@id': `${canonicalUrl}#webpage`,
                url: canonicalUrl,
                name: meta.title,
                description: meta.description,
                inLanguage: languageTag,
                isPartOf: { '@id': `${SITE_URL}/#website` },
                about: { '@id': `${SITE_URL}/#organization` },
                dateModified: '2026-04-02',
            },
        ];

        if (routeKey && routeKey !== 'home') {
            graph.push({
                '@type': 'BreadcrumbList',
                '@id': `${canonicalUrl}#breadcrumb`,
                itemListElement: [
                    {
                        '@type': 'ListItem',
                        position: 1,
                        name: ROUTE_LABELS[lang].home,
                        item: toAbsoluteUrl(pathsForLang.home),
                    },
                    {
                        '@type': 'ListItem',
                        position: 2,
                        name: ROUTE_LABELS[lang][routeKey],
                        item: canonicalUrl,
                    },
                ],
            });
        }

        if (routeKey === 'services') {
            graph.push({
                '@type': 'ItemList',
                '@id': `${canonicalUrl}#services-list`,
                name: lang === 'pt' ? 'Serviços da Monetique' : 'Monetique Services',
                itemListElement: [
                    { '@type': 'ListItem', position: 1, name: lang === 'pt' ? 'Finanças Estratégicas & Desempenho de Dados' : 'Strategic Finance & Data Performance' },
                    { '@type': 'ListItem', position: 2, name: lang === 'pt' ? 'Parceria de Negócios & Estratégia Executiva' : 'Business Partnering & Executive Strategy' },
                    { '@type': 'ListItem', position: 3, name: lang === 'pt' ? 'Tecnologia & Finanças do Futuro' : 'Technology & Future Finance' },
                    { '@type': 'ListItem', position: 4, name: lang === 'pt' ? 'Consultoria Global & Expansão' : 'Global Advisory & Expansion' },
                ],
            });
        }

        if (routeKey === 'about') {
            TEAM_SCHEMA.forEach((member) => {
                graph.push({
                    '@type': 'Person',
                    '@id': `${SITE_URL}/#${member.id}`,
                    name: member.name,
                    jobTitle: member.jobTitle,
                    worksFor: { '@id': `${SITE_URL}/#organization` },
                    url: canonicalUrl,
                });
            });
        }

        if (routeKey === 'school') {
            graph.push({
                '@type': 'EducationalOrganization',
                '@id': `${canonicalUrl}#school`,
                name: 'Monetique School',
                description: lang === 'pt'
                    ? 'Braço educacional da Monetique para comunicação executiva, liderança e business English para profissionais de finanças e negócios.'
                    : 'Educational arm of Monetique for executive communication, leadership and business English for finance and business professionals.',
                parentOrganization: { '@id': `${SITE_URL}/#organization` },
                url: canonicalUrl,
            });
        }

        if (routeKey === 'contact') {
            graph.push({
                '@type': 'ContactPage',
                '@id': `${canonicalUrl}#contact`,
                url: canonicalUrl,
                name: meta.title,
                isPartOf: { '@id': `${SITE_URL}/#website` },
                about: { '@id': `${SITE_URL}/#organization` },
            });
        }

        document.documentElement.setAttribute('lang', languageTag);
        document.title = meta.title;
        setMeta('description', meta.description);
        setMeta('author', SITE_TITLE);
        setMeta('robots', shouldIndex
            ? 'index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1'
            : 'noindex,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1');
        const ogImageAlt = lang === 'pt'
            ? 'Monetique — Consultoria Financeira Estrategica, FP&A e CFO Advisory em Sao Paulo'
            : 'Monetique — Strategic Finance Consulting, FP&A & CFO Advisory in Sao Paulo';
        setMeta('twitter:card', 'summary_large_image');
        setMeta('twitter:image', SITE_OG_IMAGE);
        setMeta('twitter:image:alt', ogImageAlt);
        setMeta('og:title', meta.title, true);
        setMeta('og:description', meta.description, true);
        setMeta('og:url', canonicalUrl, true);
        setMeta('og:site_name', SITE_TITLE, true);
        setMeta('og:type', 'website', true);
        setMeta('og:image', SITE_OG_IMAGE, true);
        setMeta('og:image:type', 'image/jpeg', true);
        setMeta('og:image:width', '1200', true);
        setMeta('og:image:height', '630', true);
        setMeta('og:image:alt', ogImageAlt, true);
        setMeta('og:locale', locale, true);
        setMeta('og:locale:alternate', alternateLocale, true);
        setMeta('twitter:title', meta.title);
        setMeta('twitter:description', meta.description);
        setLink({ rel: 'canonical', href: canonicalUrl });
        if (routeKey) {
            setLink({ rel: 'alternate', href: enUrl, hreflang: 'en' });
            setLink({ rel: 'alternate', href: ptUrl, hreflang: 'pt-BR' });
            setLink({ rel: 'alternate', href: ptUrl, hreflang: 'x-default' });
        } else {
            removeLink({ rel: 'alternate', hreflang: 'en' });
            removeLink({ rel: 'alternate', hreflang: 'pt-BR' });
            removeLink({ rel: 'alternate', hreflang: 'x-default' });
        }
        setStructuredData({
            '@context': 'https://schema.org',
            '@graph': graph,
        });
    }, [location.pathname]);

    useLayoutEffect(() => {
        const scrollToTop = () => {
            window.scrollTo(0, 0);
            document.documentElement.scrollTop = 0;
            document.body.scrollTop = 0;
            const appRoot = document.querySelector('.app');
            if (appRoot) {
                appRoot.scrollTo(0, 0);
            }
        };

        if (document.activeElement && document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
        }
        if (appRef.current) {
            appRef.current.focus({ preventScroll: true });
        }
        scrollToTop();
        const rafId = window.requestAnimationFrame(scrollToTop);
        return () => window.cancelAnimationFrame(rafId);
    }, [location.pathname, location.search, location.hash]);

    return (
        <UserContext.Provider value={{ user: user, setUser: setUser }}>
            <div className="background-wrapper">
            <Header />
            <ScrollToTop />
            <main className='app' ref={appRef} tabIndex={-1}>
                <div className="page-load" key={`${location.pathname}${location.search}${location.hash}`}>
                    <Outlet />
                </div>
            </main>
            <Footer />
            </div>
        </UserContext.Provider>
    );
};

const buildRoutesForLang = (lang) => ({
    path: lang === DEFAULT_LANG ? '/' : `/${lang}`,
    element: <AppLayout />,
    children: [
        { index: true, element: <Main /> },
        { path: ROUTE_SLUGS.about[lang], element: <About /> },
        { path: ROUTE_SLUGS.beliefs[lang], element: <Beliefs /> },
        { path: ROUTE_SLUGS.services[lang], element: <Service /> },
        { path: ROUTE_SLUGS.school[lang], element: <School /> },
        { path: ROUTE_SLUGS.contact[lang], element: <Contact /> },
        { path: ROUTE_SLUGS.privacy[lang], element: <Privacy /> },
        { path: ROUTE_SLUGS.disclaimer[lang], element: <Disclaimer /> },
        { path: '*', element: <NotFound /> },
    ],
    errorElement: <Error />,
});

const LegacyPtRedirect = () => {
    const location = useLocation();
    return <Navigate to={switchLangPath(location.pathname, DEFAULT_LANG)} replace />;
};

const appRouter = createBrowserRouter([
    buildRoutesForLang(DEFAULT_LANG),
    buildRoutesForLang('en'),
    { path: '/pt/*', element: <LegacyPtRedirect /> },
]);

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(<RouterProvider router={appRouter} />);
